import { useState } from 'react';
import type { BillingProfileRow, CursorPage } from '@dru/types';
import {
  Alert, Button, Checkbox, DataTable, IconAction, InfoTip, RowActions, SearchSelect, StatusBadge, TextField, useToast,
} from '@dru/ui';
import { Pencil, X } from 'lucide-react';
import { api } from '../../../lib/api';
import { useQuery } from '../../../lib/useQuery';
import { errorMessage, useAuth } from '../../../lib/auth';
import { holds } from '../../../layouts/ConsoleShell';
import { Dialog } from '../../../components/Dialog';

type TestBrief = { id: string; code: string; name: string };

/** The whole test catalogue for the picker, a page at a time. */
async function allTests(): Promise<TestBrief[]> {
  const out: TestBrief[] = [];
  let cursor: string | null = null;
  for (let page = 0; page < 10; page += 1) {
    const res: CursorPage<TestBrief> = await api.get(`/tests?brief=true&limit=200${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`);
    out.push(...res.items);
    if (!res.nextCursor) break;
    cursor = res.nextCursor;
  }
  return out;
}

/**
 * The billing profile master.
 *
 * A billing profile is a named set of tests sold and paid for as one item.
 * Its contents are listed explicitly here, and are separate from report
 * sections: where a test prints never decides its price. Once a published
 * rate card prices a profile its tests are locked — a new profile is made for
 * new contents — so an agreed price always meant the same tests.
 */
export function BillingProfiles() {
  const { permissions } = useAuth();
  const canManage = holds(permissions, 'settlement.rate-manage');
  const canTests = holds(permissions, 'test.read');
  const { toast } = useToast();
  const [showInactive, setShowInactive] = useState(false);
  const list = useQuery<{ items: BillingProfileRow[] }>(
    () => api.get(`/billing-profiles${showInactive ? '?includeInactive=true' : ''}`), [showInactive],
  );
  const [editing, setEditing] = useState<BillingProfileRow | 'new' | null>(null);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-small text-neutral-600">
          A profile is paid once when all its tests are resulted on a visit. A test inside a billed profile is not charged again.
          <InfoTip label="About billing profiles">
            Price a profile on a lab's rate card (what the lab is paid) and on a customer rate card (what the company is
            charged). Tests outside a profile are charged at their own rates. Report sections such as "Liver" or "Urine"
            do not affect pricing.
          </InfoTip>
        </div>
        <div className="flex items-center gap-3">
          <Checkbox label="Show inactive" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
          {canManage && canTests && <Button size="sm" onClick={() => setEditing('new')}>New profile</Button>}
        </div>
      </div>

      <DataTable<BillingProfileRow>
        rows={list.data?.items ?? []}
        loading={list.loading}
        error={list.error}
        onRetry={list.reload}
        rowKey={(p) => p.id}
        empty={{
          title: 'No billing profiles yet',
          hint: 'Create one for each set of tests a lab or company has agreed a single price for, such as a CBC.',
          action: canManage && canTests ? <Button onClick={() => setEditing('new')}>New profile</Button> : undefined,
        }}
        columns={[
          { key: 'name', header: 'Profile', alwaysVisible: true, cell: (p) => (
            <div>
              <div className="text-sm font-medium text-neutral-900">{p.name}</div>
              <div className="font-mono text-caption text-neutral-500">{p.code} · version {p.version}</div>
            </div>
          ) },
          { key: 'tests', header: 'Tests included', cell: (p) => (
            <p className="max-w-xl text-xs text-neutral-700">
              <span className="font-medium">{p.tests.length}:</span> {p.tests.map((t) => t.name).join(', ')}
            </p>
          ) },
          { key: 'used', header: 'Priced on', cell: (p) => (
            <span className="text-xs text-neutral-600">
              {p.partnerRates} lab rate{p.partnerRates === 1 ? '' : 's'} · {p.customerPrices} customer price{p.customerPrices === 1 ? '' : 's'}
            </span>
          ) },
          { key: 'status', header: 'Status', mobileLabel: false, cell: (p) => (
            <div className="flex flex-wrap gap-1">
              <StatusBadge tone={p.isActive ? 'covered' : 'neutral'}>{p.isActive ? 'Active' : 'Inactive'}</StatusBadge>
              {p.contentsLocked && <StatusBadge tone="info">Tests locked</StatusBadge>}
            </div>
          ) },
          { key: 'act', header: 'Actions', align: 'right', alwaysVisible: true, mobileLabel: '', cell: (p) => (
            canManage ? (
              <RowActions>
                <IconAction label={`Edit ${p.name}`} icon={Pencil} onClick={() => setEditing(p)} />
              </RowActions>
            ) : null
          ) },
        ]}
      />

      {editing && (
        <ProfileDialog
          profile={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onDone={(msg) => { setEditing(null); list.reload(); toast('success', msg); }}
        />
      )}
    </div>
  );
}

function ProfileDialog({ profile, onClose, onDone }: {
  profile: BillingProfileRow | null; onClose: () => void; onDone: (msg: string) => void;
}) {
  const locked = !!profile?.contentsLocked;
  const tests = useQuery<TestBrief[]>(() => (locked ? Promise.resolve([]) : allTests()), [locked]);
  const [code, setCode] = useState(profile?.code ?? '');
  const [name, setName] = useState(profile?.name ?? '');
  const [description, setDescription] = useState(profile?.description ?? '');
  const [active, setActive] = useState(profile?.isActive ?? true);
  const [chosen, setChosen] = useState<TestBrief[]>(profile?.tests ?? []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [key] = useState(() => crypto.randomUUID());

  const codeOk = /^[A-Z0-9][A-Z0-9_-]{1,39}$/.test(code.trim().toUpperCase());
  const valid = name.trim().length >= 2 && chosen.length > 0 && (profile ? true : codeOk);

  async function save() {
    setBusy(true); setError(null);
    try {
      if (profile) {
        await api.patch(`/billing-profiles/${profile.id}`, {
          name: name.trim(), description: description.trim(), isActive: active,
          ...(locked ? {} : { testIds: chosen.map((t) => t.id) }),
        });
        onDone(`${name.trim()} saved.`);
      } else {
        await api.post('/billing-profiles', {
          code: code.trim().toUpperCase(), name: name.trim(),
          description: description.trim() || undefined, testIds: chosen.map((t) => t.id),
        }, key);
        onDone(`${name.trim()} created. Price it on a lab or customer rate card.`);
      }
    } catch (e) { setError(errorMessage(e)); setBusy(false); }
  }

  return (
    <Dialog title={profile ? `Edit ${profile.name}` : 'New billing profile'} onClose={onClose} wide>
      <div className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <TextField label="Name" value={name} onChange={setName} required autoFocus placeholder="e.g. Liver function test" />
          <TextField
            label="Code" value={code} onChange={(v) => setCode(v.toUpperCase())} required disabled={!!profile}
            placeholder="e.g. LFT" hint={profile ? 'A code cannot change.' : 'Capital letters, digits, - or _.'}
          />
        </div>
        <TextField label="Description (optional)" value={description} onChange={setDescription} />

        <div className="space-y-2">
          <div className="text-small font-medium text-neutral-800">Tests included ({chosen.length})</div>
          {locked ? (
            <Alert tone="info">
              A published rate card prices this profile, so its tests are locked. To change them, create a new profile
              and price it on a new card.
            </Alert>
          ) : (
            <SearchSelect
              aria-label="Add a test" value="" onChange={(id) => {
                const t = tests.data?.find((x) => x.id === id);
                if (t && !chosen.some((c) => c.id === t.id)) setChosen((cs) => [...cs, t]);
              }}
              placeholder={tests.loading ? 'Loading tests…' : 'Add a test…'} searchPlaceholder="Search tests by name or code…"
              options={(tests.data ?? []).filter((t) => !chosen.some((c) => c.id === t.id)).map((t) => ({ value: t.id, label: t.name, hint: t.code }))}
            />
          )}
          {tests.error && <Alert>{tests.error}</Alert>}
          <ul className="flex flex-wrap gap-1.5">
            {chosen.map((t) => (
              <li key={t.id} className="inline-flex items-center gap-1 rounded-full border border-neutral-200 bg-neutral-50 py-0.5 pl-2.5 pr-1 text-xs text-neutral-800">
                {t.name} <span className="font-mono text-neutral-500">{t.code}</span>
                {!locked && (
                  <button
                    type="button" aria-label={`Remove ${t.name}`}
                    className="rounded-full p-1 text-neutral-500 hover:bg-neutral-200 hover:text-neutral-800 pointer-coarse:p-2"
                    onClick={() => setChosen((cs) => cs.filter((c) => c.id !== t.id))}
                  >
                    <X className="h-3 w-3" aria-hidden />
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>

        {profile && <Checkbox label="Active (offered on new rate cards)" checked={active} onChange={(e) => setActive(e.target.checked)} />}
        {error && <Alert>{error}</Alert>}
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={!valid || busy} loading={busy}>{profile ? 'Save' : 'Create profile'}</Button>
        </div>
      </div>
    </Dialog>
  );
}
